import { restoreFocusWithoutRing } from './modalAccessibility';
import type { Root } from './prototypeTypes';

type CloseOptions = { restoreFocus?: boolean; suppressFocusRing?: boolean };
type MenuBinding = { host: HTMLElement; destroy: () => void };

const bindings = new WeakMap<Root, MenuBinding>();
const disabledRoots = new WeakSet<Root>();

function elements(root: Root): { host: HTMLElement; trigger: HTMLButtonElement; menu: HTMLElement; current: HTMLElement | null } | null {
	const trigger = root.querySelector<HTMLButtonElement>('#browseIndustryTrigger');
	const menu = root.querySelector<HTMLElement>('#browseIndustryMenu');
	const host = trigger?.closest<HTMLElement>('.browse-industry-mobile');
	if (!trigger || !menu || !host) return null;
	return { host, trigger, menu, current: root.querySelector<HTMLElement>('#browseIndustryCurrent') };
}

function options(menu: HTMLElement): HTMLElement[] {
	return Array.from(menu.querySelectorAll<HTMLElement>('[role="option"]'));
}

function isOpen(root: Root): boolean {
	return elements(root)?.menu.hidden === false;
}

function updateMenuHeight(root: Root): void {
	if (typeof window === 'undefined' || !isOpen(root)) return;
	const menuElements = elements(root);
	if (!menuElements) return;
	const { bottom } = menuElements.trigger.getBoundingClientRect();
	const height = Math.max(0, Math.min(360, window.innerHeight - bottom - 16));
	menuElements.menu.style.setProperty('--browse-industry-menu-max-height', `${height}px`);
}

function focusOption(option: HTMLElement): void {
	option.focus();
	option.scrollIntoView?.({ block: 'nearest' });
}

function openBrowseIndustryMenu(root: Root): void {
	const menuElements = elements(root);
	if (!menuElements) return;
	const { menu, trigger } = menuElements;
	menu.hidden = false;
	trigger.setAttribute('aria-expanded', 'true');
	updateMenuHeight(root);
	const current = options(menu).find((option) => option.getAttribute('aria-selected') === 'true') ?? options(menu)[0];
	if (current) {
		options(menu).forEach((option) => { option.tabIndex = option === current ? 0 : -1; });
		focusOption(current);
	}
}

/** Attaches the mobile industry menu's local interaction behavior once per live host. */
export function wireBrowseIndustryMenu(root: Root): void {
	if (disabledRoots.has(root)) return;
	const menuElements = elements(root);
	if (!menuElements) return;
	const existing = bindings.get(root);
	if (existing?.host === menuElements.host) return;
	existing?.destroy();

	const { host, trigger, menu } = menuElements;
	const AbortControllerForHost = host.ownerDocument.defaultView?.AbortController ?? AbortController;
	const controller = new AbortControllerForHost();
	const { signal } = controller;
	let frameId: number | null = null;
	const requestFrame = typeof window === 'undefined'
		? null
		: window.requestAnimationFrame?.bind(window) ?? ((callback: FrameRequestCallback) => window.setTimeout(() => callback(Date.now()), 0));
	const cancelFrame = typeof window === 'undefined'
		? null
		: window.cancelAnimationFrame?.bind(window) ?? window.clearTimeout.bind(window);
	const scheduleViewportUpdate = () => {
		if (signal.aborted || !isOpen(root) || frameId !== null || !requestFrame) return;
		frameId = requestFrame(() => {
			frameId = null;
			if (!signal.aborted) updateMenuHeight(root);
		});
	};
	const destroy = () => {
		if (frameId !== null && cancelFrame) cancelFrame(frameId);
		frameId = null;
		controller.abort();
		host.removeAttribute('data-browse-industry-menu-bound');
		if (bindings.get(root)?.host === host) bindings.delete(root);
	};
	bindings.set(root, { host, destroy });
	host.dataset.browseIndustryMenuBound = 'true';

	trigger.addEventListener('click', () => {
		if (menu.hidden) openBrowseIndustryMenu(root);
		else closeBrowseIndustryMenu(root, { restoreFocus: true, suppressFocusRing: true });
	}, { signal });

	menu.addEventListener('keydown', (event) => {
		if (event.key === 'Escape') {
			event.preventDefault();
			closeBrowseIndustryMenu(root, { restoreFocus: true });
			return;
		}
		const option = (event.target as HTMLElement | null)?.closest<HTMLElement>('[role="option"]');
		if (!option) return;
		const menuOptions = options(menu);
		const index = menuOptions.indexOf(option);
		if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
			event.preventDefault();
			const next = event.key === 'ArrowDown' ? (index + 1) % menuOptions.length : (index - 1 + menuOptions.length) % menuOptions.length;
			menuOptions.forEach((item, itemIndex) => { item.tabIndex = itemIndex === next ? 0 : -1; });
			focusOption(menuOptions[next]);
			return;
		}
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			option.click();
		}
	}, { signal });

	root.addEventListener('pointerdown', (event) => {
		const target = event.target as Node | null;
		if (target && !host.contains(target)) closeBrowseIndustryMenu(root);
	}, { signal });
	root.addEventListener('focusin', (event) => {
		const target = event.target as Node | null;
		if (target && !host.contains(target)) closeBrowseIndustryMenu(root);
	}, { signal });

	if (typeof window !== 'undefined') {
		window.addEventListener('resize', () => updateMenuHeight(root), { passive: true, signal });
		window.addEventListener('scroll', (event) => {
			const target = event.target;
			if (target && typeof target === 'object' && 'nodeType' in target && menu.contains(target as Node)) return;
			scheduleViewportUpdate();
		}, { capture: true, passive: true, signal });
	}
}

/** Removes all menu listeners and pending viewport work for this root. */
export function destroyBrowseIndustryMenu(root: Root): void {
	disabledRoots.add(root);
	bindings.get(root)?.destroy();
	const menuElements = elements(root);
	if (!menuElements) return;
	const activeElement = menuElements.menu.ownerDocument.activeElement;
	if (activeElement && menuElements.menu.contains(activeElement) && typeof (activeElement as HTMLElement).blur === 'function') {
		(activeElement as HTMLElement).blur();
	}
	menuElements.menu.hidden = true;
	menuElements.trigger.setAttribute('aria-expanded', 'false');
}

/** Starts a new controller lifecycle after a prior teardown for this root. */
export function enableBrowseIndustryMenu(root: Root): void {
	disabledRoots.delete(root);
}

/** Keeps the compact mobile projection aligned with the committed Browse filter. */
export function syncBrowseIndustryMenu(root: Root, activeFilter: string): void {
	const menuElements = elements(root);
	if (!menuElements) return;
	const menuOptions = options(menuElements.menu);
	const selected = menuOptions.find((option) => option.dataset.browseCategory === activeFilter) ?? menuOptions.find((option) => option.dataset.browseCategory === 'all') ?? menuOptions[0];
	menuOptions.forEach((option) => {
		const active = option === selected;
		option.setAttribute('aria-selected', String(active));
		option.tabIndex = active ? 0 : -1;
	});
	if (selected) {
		const label = selected.textContent?.trim() ?? '';
		if (menuElements.current) menuElements.current.textContent = label;
		menuElements.trigger.setAttribute('aria-label', `Category: ${label}`);
	}
}

/** Hides the menu without mutating the selected category or browse history. */
export function closeBrowseIndustryMenu(root: Root, options: CloseOptions = {}): void {
	const menuElements = elements(root);
	if (!menuElements) return;
	const { menu, trigger } = menuElements;
	menu.hidden = true;
	trigger.setAttribute('aria-expanded', 'false');
	if (!options.restoreFocus || !trigger.isConnected || trigger.closest('[hidden]')) return;
	// Pointer-driven closes (trigger toggle, option pick) re-park focus on the
	// trigger; suppress the device keyboard-access halo. Escape keeps the plain
	// focus so keyboard users see their ring.
	if (options.suppressFocusRing) restoreFocusWithoutRing(trigger);
	else trigger.focus();
}
